// Hover for a HollowScript editor: the type of what is under the cursor, as the
// native checker renders it, with Studio's own documentation merged in.
//
// The native analysis handle's `hover(offset)` answers the type of the
// smallest expression at the offset - the checker's *inferred* type, so a
// `constant total = 1 + 2` hovers as `Int` with no annotation written, which
// the TypeScript package never could. Studio adds what the language does not
// carry:
//
//   - the capability surface: a `uses`-able name's signatures, and whether the
//     project grants it (`capabilityCatalog.ts`, itself built from the native
//     declarations);
//   - the standard library's prose (`stdlib.ts`), narrowed to the receiver's
//     real type now that the checker can say what the receiver is, where it
//     used to have to list every candidate;
//   - keywords, from the lexer's own table, which the native hover (an
//     expression-only service) answers `null` for;
//   - where an imported name was declared, from `definition()`.
//
// A hover on the *name* of a member (`map` in `items.map(...)`) is answered by
// the enclosing call's type, which would read as "map has type [Int]"; so the
// native type is shown for a member only when the expression it describes ends
// where the word does (a field read or an uncalled method), never for a call.
//
// Returns plain data - see `tokenize.ts`'s own doc comment for why no
// `monaco-editor` import belongs in this module.

import type { NativeModules } from '../native/hollowscript.js';
import type { StudioCapabilityName } from '../capabilityNames.js';
import { DEFAULT_ENTRY_PATH, withAnalysis } from './analysis.js';
import { capabilityMembers, capabilityNamespaceDetail, grantedCapabilityNames, loadCapabilityCatalog } from './capabilityCatalog.js';
import { STDLIB_ALL_FREE_FUNCTIONS, stdlibMemberName, stdlibMembersNamed, stdlibReceiverForType } from './stdlib.js';

export interface HollowScriptHoverParams {
  readonly source: string;
  /** A UTF-16 offset into `source` (Monaco's `model.getOffsetAt`). */
  readonly offset: number;
  readonly grantedCapabilities: ReadonlySet<StudioCapabilityName> | undefined;
  readonly entryPath?: string;
  readonly modules?: NativeModules;
  /** The editor model this belongs to, so it shares that model's one live
   * analysis handle - see `analysis.ts`. */
  readonly key?: string;
}

export interface HollowScriptHoverInfo {
  /** Rendered as separate Markdown blocks by the Monaco adapter (one string
   * per entry of the hover's contents array) - kept as plain strings here so
   * this module carries no `monaco-editor` dependency. */
  readonly markdownLines: readonly string[];
  /** What the hover is about, as a half-open UTF-16 range of `source`: the
   * word under the cursor, or the expression the checker typed. */
  readonly start: number;
  readonly end: number;
}

const WORD_CHAR = /[A-Za-z0-9_]/;

/** The identifier-shaped word around `offset`, if any. An offset at either
 * edge of the word counts as inside it, matching `getWordAtPosition`. */
function wordAround(source: string, offset: number): { word: string; start: number; end: number } | undefined {
  let start = Math.min(offset, source.length);
  let end = start;
  while (start > 0 && WORD_CHAR.test(source[start - 1]!)) start -= 1;
  while (end < source.length && WORD_CHAR.test(source[end]!)) end += 1;
  const word = source.slice(start, end);
  // A word that starts with a digit is a number literal, not a name.
  if (word === '' || /^[0-9]/.test(word)) return undefined;
  return { word, start, end };
}

const FREE_FUNCTION_DOCS = new Map(STDLIB_ALL_FREE_FUNCTIONS.map((member) => [stdlibMemberName(member), member]));

/** Hover info for the symbol at `params.offset`, or `null` when there is
 * nothing to say about it. Never throws on any text: the native build
 * analyses a program with errors all the same. */
export async function computeHollowScriptHover(params: HollowScriptHoverParams): Promise<HollowScriptHoverInfo | null> {
  const { source, grantedCapabilities: granted } = params;
  const entryPath = params.entryPath ?? DEFAULT_ENTRY_PATH;
  const catalog = await loadCapabilityCatalog();
  const around = wordAround(source, params.offset);
  // Ask the checker about a character of the word itself, not the gap beside it.
  const queryOffset = around ? Math.min(Math.max(params.offset, around.start), around.end - 1) : params.offset;

  const answered = await withAnalysis(
    { source, entryPath, grantedCapabilities: granted, modules: params.modules, key: params.key },
    (analysis): HollowScriptHoverInfo | null => {
      const native = analysis.hover(queryOffset);
      const start = around ? around.start : (native?.start ?? params.offset);
      const end = around ? around.end : (native?.end ?? params.offset);

      if (!around) return native ? { markdownLines: [`\`${native.type}\``], start, end } : null;
      const { word } = around;

      // `receiver.word`: the part of the language Studio documents.
      const dot = around.start - 1;
      if (dot >= 0 && source[dot] === '.' && source[dot - 1] !== '.') {
        const receiverName = /([A-Za-z_][A-Za-z0-9_]*)$/.exec(source.slice(0, dot))?.[1];
        if (receiverName !== undefined) {
          const capabilityEntry = catalog.entries.get(receiverName);
          const member = capabilityEntry ? capabilityMembers(catalog, receiverName).find((m) => m.name === word) : undefined;
          if (member) {
            const isGranted = (grantedCapabilityNames(granted) as readonly string[]).includes(receiverName);
            return {
              markdownLines: [
                `\`${receiverName}.${member.signature}\``,
                isGranted
                  ? `Capability method - requires \`uses ${receiverName}\`.`
                  : `Capability method - **not granted**. Add \`${receiverName}\` to this project's scripting capabilities, or add \`uses ${receiverName}\`.`,
              ],
              start,
              end,
            };
          }
        }
        const receiverType = dot > 0 ? analysis.hover(dot - 1)?.type : undefined;
        const group = receiverType === undefined ? undefined : stdlibReceiverForType(receiverType);
        // A receiver whose type is known but is neither a String nor a list has
        // no documented members here: none beats a wrong candidate.
        const candidates = receiverType !== undefined && group === undefined ? [] : stdlibMembersNamed(word, group);
        const lines: string[] = [];
        // A field read or an uncalled method: the checker typed exactly the word.
        if (native && native.end === around.end) lines.push(`\`${word}\`: ${native.type}`);
        for (const candidate of candidates) lines.push(`\`${candidate.receiver}.${candidate.signature}\`  \n${candidate.doc}`);
        return lines.length > 0 ? { markdownLines: lines, start, end } : null;
      }

      const keyword = catalog.keywords.includes(word);
      if (keyword) return { markdownLines: [`\`${word}\` - HollowScript keyword.`], start, end };

      const entry = catalog.entries.get(word);
      if (entry) {
        const isGranted = (grantedCapabilityNames(granted) as readonly string[]).includes(word);
        return {
          markdownLines: [
            `\`${word}\` - ${capabilityNamespaceDetail(catalog, word) ?? 'capability'}`,
            isGranted ? 'Granted to this project.' : `Not granted to this project. Add it to Game Setup's scripting capabilities, or add \`uses ${word}\`.`,
          ],
          start,
          end,
        };
      }

      const lines: string[] = [];
      const free = FREE_FUNCTION_DOCS.get(word);
      if (free) lines.push(`\`${free.signature}\``, free.doc);
      else if (native) lines.push(`\`${word}\`: ${native.type}`);

      // A declaration's own name is not an expression, so the checker has no
      // type to hover; the outline knows it (top-level declarations only).
      if (lines.length === 0) {
        const own = analysis.documentSymbols().find((symbol) => symbol.selStart <= queryOffset && queryOffset <= symbol.selEnd && symbol.name === word);
        if (own) lines.push(own.detail === '' ? `\`${word}\` - ${own.kind}` : `\`${word}\`: ${own.detail}`);
      }
      if (lines.length === 0) return null;

      const definition = analysis.definition(queryOffset);
      if (definition && definition.file !== entryPath) lines.push(`Declared in \`${definition.file}\`.`);
      return { markdownLines: lines, start, end };
    },
  );
  return answered ?? null;
}
